import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Calendar, Clock, MapPin, Users, Globe, Lock, Group, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EventForm {
  title: string;
  description: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  timezone: string;
  location: string;
  location_details: {
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  max_attendees: string;
  visibility: 'public' | 'private' | 'group';
  group_id?: string;
  cover_image?: File;
  tags: string[];
}

export function CreateEvent() {
  const navigate = useNavigate();
  const [form, setForm] = useState<EventForm>({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: '',
    location_details: {},
    max_attendees: '',
    visibility: 'public',
    tags: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setForm(prev => ({ ...prev, cover_image: file }));
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      setForm(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate dates
      const startDateTime = new Date(`${form.start_date}T${form.start_time}`);
      const endDateTime = new Date(`${form.end_date}T${form.end_time}`);

      if (endDateTime <= startDateTime) {
        throw new Error('End date must be after start date');
      }

      let coverUrl;
      if (form.cover_image) {
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('event-covers')
          .upload(`${Date.now()}-${form.cover_image.name}`, form.cover_image);

        if (uploadError) throw uploadError;
        coverUrl = `${process.env.SUPABASE_URL}/storage/v1/object/public/event-covers/${uploadData.path}`;
      }

      const { data: event, error: eventError } = await supabase
        .from('events')
        .insert({
          title: form.title,
          description: form.description,
          start_date: startDateTime.toISOString(),
          end_date: endDateTime.toISOString(),
          timezone: form.timezone,
          location: form.location,
          location_details: form.location_details,
          max_attendees: form.max_attendees ? parseInt(form.max_attendees) : null,
          visibility: form.visibility,
          group_id: form.group_id,
          cover_image: coverUrl,
          tags: form.tags
        })
        .select()
        .single();

      if (eventError) throw eventError;

      navigate(`/events/${event.id}`);
    } catch (error) {
      console.error('Error creating event:', error);
      setError(error instanceof Error ? error.message : 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header */}
          <div className="p-6 border-b">
            <h1 className="text-2xl font-bold">Create Event</h1>
          </div>

          <div className="p-6 space-y-6">
            {error && (
              <div className="p-4 rounded-lg bg-red-50 text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Cover Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cover Image
              </label>
              <div className="relative">
                {coverPreview ? (
                  <div className="relative rounded-lg overflow-hidden h-48">
                    <img
                      src={coverPreview}
                      alt="Cover preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setCoverPreview(null);
                        setForm(prev => ({ ...prev, cover_image: undefined }));
                      }}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-royal-purple transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCoverChange}
                      className="hidden"
                    />
                    <div className="space-y-2 text-center">
                      <div className="mx-auto h-12 w-12 text-gray-400">
                        <Image className="w-12 h-12" />
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium text-royal-purple">
                          Click to upload
                        </span>
                        {' or drag and drop'}
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    title: e.target.value
                  }))}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    description: e.target.value
                  }))}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                />
              </div>
            </div>

            {/* Date and Time */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Date and Time</h3>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      start_date: e.target.value
                    }))}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      start_time: e.target.value
                    }))}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      end_date: e.target.value
                    }))}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      end_time: e.target.value
                    }))}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    timezone: e.target.value
                  }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                >
                  {Intl.supportedValuesOf('timeZone').map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Location</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Name
                </label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    location: e.target.value
                  }))}
                  placeholder="e.g., Conference Center, Virtual Meeting"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={form.location_details.address || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      location_details: {
                        ...prev.location_details,
                        address: e.target.value
                      }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={form.location_details.city || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      location_details: {
                        ...prev.location_details,
                        city: e.target.value
                      }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={form.location_details.state || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      location_details: {
                        ...prev.location_details,
                        state: e.target.value
                      }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={form.location_details.country || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      location_details: {
                        ...prev.location_details,
                        country: e.target.value
                      }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Settings</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Attendees
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.max_attendees}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    max_attendees: e.target.value
                  }))}
                  placeholder="Leave empty for unlimited"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibility
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={form.visibility === 'public'}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        visibility: e.target.value as 'public'
                      }))}
                      className="w-4 h-4 text-royal-purple focus:ring-royal-purple"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">Public</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Anyone can see and join this event
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={form.visibility === 'private'}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        visibility: e.target.value as 'private'
                      }))}
                      className="w-4 h-4 text-royal-purple focus:ring-royal-purple"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Lock className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">Private</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Only invited people can see and join this event
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="visibility"
                      value="group"
                      checked={form.visibility === 'group'}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        visibility: e.target.value as 'group'
                      }))}
                      className="w-4 h-4 text-royal-purple focus:ring-royal-purple"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Group className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">Group</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Only group members can see and join this event
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      placeholder="Add tags..."
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Add
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {form.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-royal-purple/10 text-royal-purple rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="p-0.5 hover:bg-royal-purple/20 rounded-full transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-colors",
                  loading
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-royal-purple text-white hover:bg-royal-purple/90"
                )}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </div>
                ) : (
                  'Create Event'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}